import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaProjectDiagram, FaPlus, FaEdit, FaTrash, FaUserTie, FaCalendarAlt, FaSearch, FaCheckCircle, FaExclamationCircle, FaTimes } from 'react-icons/fa';
import { supabase } from '../../supabaseClient';
import { fetchAuth } from '../../utils/api';
import './GestionProyectos.css';

function GestionProyectos() {
  const [proyectos, setProyectos] = useState([]);
  const [jefes, setJefes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('crear'); // 'crear' o 'editar'
  const [proyectoSeleccionado, setProyectoSeleccionado] = useState(null);
  const [toast, setToast] = useState({ visible: false, type: 'success', title: '', message: '' });
  const [confirmEliminarOpen, setConfirmEliminarOpen] = useState(false);
  const [proyectoParaEliminar, setProyectoParaEliminar] = useState(null);
  const [filtroJefe, setFiltroJefe] = useState('TODOS');
  const [mesSeleccionado, setMesSeleccionado] = useState('');
  const [anioSeleccionado, setAnioSeleccionado] = useState(String(new Date().getFullYear()));
  const [descMarkers, setDescMarkers] = useState('');
  
  // Form states
  const [formData, setFormData] = useState({
    nombre_proyecto: '',
    descripcion: '',
    id_jefe: '',
    fecha_inicio: '',
    fecha_fin: ''
  });

  useEffect(() => {
    fetchProyectos();
    fetchJefes();
  }, []);

  useEffect(() => {
    if (!toast.visible) return;
    const t = setTimeout(() => {
      setToast(prev => ({ ...prev, visible: false }));
    }, 3000);
    return () => clearTimeout(t);
  }, [toast.visible]);

  const fetchProyectos = async () => {
    try {
      console.log('📊 Cargando proyectos...');
      setError(null);
      const data = await fetchAuth('/api/admin/proyectos/');
        console.log('✅ Proyectos cargados:', data);
        console.log('🔍 Estados de proyectos:', data.proyectos?.map(p => ({ 
          nombre: p.nombre_proyecto, 
          estado: p.estado 
        })));
        // Usar los estados reales del backend
        setProyectos(data.proyectos || []);
      
    } catch (err) {
      console.error('❌ Error al cargar proyectos:', err);
      if (err.status === 403) {
        setError('Acceso denegado. Inicia sesión con una cuenta de Administrador.');
      } else {
        setError(err.message || 'Error al cargar proyectos');
      }
    } finally {
      setLoading(false);
    }
  };

  const cleanDescription = (d) => {
    let s = String(d || '');
    s = s.replace(/\[ETAPA_ACTUAL:\d+\]/g, '');
    s = s.replace(/\[ETAPA_CERRADA:\d+(?::\d{4}-\d{2}-\d{2})?\]/g, '');
    s = s.replace(/\[INVITACION_USUARIO:\d+:(ACEPTADA|RECHAZADA)\]/g, '');
    s = s.replace(/\[PROYECTO_CERRADO:\d{4}-\d{2}-\d{2}\]/g, '');
    s = s.trim();
    s = s.replace(/\n{3,}/g, '\n\n');
    return s;
  };

  const extractMarkers = (d) => {
    const src = String(d || '');
    const parts = [];
    const m1 = src.match(/\[ETAPA_ACTUAL:\d+\]/g) || [];
    const m2 = src.match(/\[ETAPA_CERRADA:\d+(?::\d{4}-\d{2}-\d{2})?\]/g) || [];
    const m3 = src.match(/\[INVITACION_USUARIO:\d+:(ACEPTADA|RECHAZADA)\]/g) || [];
    const m4 = src.match(/\[PROYECTO_CERRADO:\d{4}-\d{2}-\d{2}\]/g) || [];
    parts.push(...m1, ...m2, ...m3, ...m4);
    return parts.join('\n');
  };

  const fetchJefes = async () => {
    try {
      console.log('👥 Cargando jefes de proyecto...');
      setError(null);
      const data = await fetchAuth('/api/admin/usuarios/');
        console.log('📋 Todos los usuarios:', data.usuarios);
        // Filtrar solo los jefes de proyecto (id_rol = 2)
        const jefesFiltrados = (data.usuarios || []).filter(u => u.id_rol === 2);
        console.log('✅ Jefes filtrados (id_rol=2):', jefesFiltrados);
        setJefes(jefesFiltrados);
      
    } catch (err) {
      console.error('❌ Error al cargar jefes:', err);
      if (err.status === 403) {
        setError('Acceso denegado. Inicia sesión con una cuenta de Administrador.');
      } else {
        setError(err.message || 'Error al cargar jefes');
      }
    }
  };

  const handleCrearProyecto = async (e) => {
    e.preventDefault();
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      console.log('📝 Creando proyecto con datos:', formData);

      if (formData.fecha_fin && formData.fecha_inicio && formData.fecha_fin < formData.fecha_inicio) {
        setToast({ visible: true, type: 'error', title: 'Fechas inválidas', message: 'La fecha plazo no puede ser menor a la fecha de creación.' });
        return;
      }

      // Cambiar id_jefe por id_usuario
      const dataToSend = {
        nombre_proyecto: formData.nombre_proyecto,
        descripcion: [cleanDescription(formData.descripcion), descMarkers].filter(Boolean).join('\n').trim(),
        id_usuario: formData.id_jefe,  // ← CAMBIO: enviar como id_usuario
        fecha_inicio: formData.fecha_inicio || null,
        fecha_fin: formData.fecha_fin || null
      };

      console.log('📤 Enviando al backend:', dataToSend);

      const response = await fetch('/api/admin/proyectos/crear/', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSend)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Error del backend:', errorData);
        throw new Error(errorData.error || 'Error al crear proyecto');
      }

      const result = await response.json();
      console.log('✅ Proyecto creado:', result);

      handleCloseModal();
      setToast({ visible: true, type: 'success', title: 'Proyecto creado', message: 'El proyecto fue creado exitosamente.' });
      await fetchProyectos();
    } catch (err) {
      setToast({ visible: true, type: 'error', title: 'No se pudo crear', message: err.message || 'Intenta nuevamente.' });
      console.error('❌ Error completo:', err);
    }
  };

  const handleActualizarProyecto = async (e) => {
    e.preventDefault();
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (formData.fecha_fin && formData.fecha_inicio && formData.fecha_fin < formData.fecha_inicio) {
        setToast({ visible: true, type: 'error', title: 'Fechas inválidas', message: 'La fecha plazo no puede ser menor a la fecha de creación.' });
        return;
      }

      // Cambiar id_jefe por id_usuario
      const dataToSend = {
        nombre_proyecto: formData.nombre_proyecto,
        descripcion: [cleanDescription(formData.descripcion), descMarkers].filter(Boolean).join('\n').trim(),
        id_usuario: formData.id_jefe,  // ← CAMBIO: enviar como id_usuario
        fecha_inicio: formData.fecha_inicio || null,
        fecha_fin: formData.fecha_fin || null
      };

      const response = await fetch(`/api/admin/proyectos/${proyectoSeleccionado.id_proyecto}/actualizar/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSend)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al actualizar proyecto');
      }

      await fetchProyectos();
      handleCloseModal();
      setToast({ visible: true, type: 'success', title: 'Proyecto actualizado', message: 'Los cambios fueron guardados.' });
    } catch (err) {
      setToast({ visible: true, type: 'error', title: 'No se pudo actualizar', message: err.message || 'Intenta nuevamente.' });
      console.error('Error:', err);
    }
  };

  const handleEliminarProyectoOpen = (proyecto) => {
    setProyectoParaEliminar(proyecto);
    setConfirmEliminarOpen(true);
  };

  const closeConfirmEliminar = () => {
    setConfirmEliminarOpen(false);
    setProyectoParaEliminar(null);
  };

  const confirmEliminarProyecto = async () => {
    if (!proyectoParaEliminar) return;
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      const response = await fetch(`/api/admin/proyectos/${proyectoParaEliminar.id_proyecto}/eliminar/`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        await fetchProyectos();
        closeConfirmEliminar();
        setToast({ visible: true, type: 'success', title: 'Proyecto eliminado', message: 'El proyecto fue eliminado exitosamente.' });
      } else {
        const error = await response.json();
        setToast({ visible: true, type: 'error', title: 'No se pudo eliminar', message: error.error || 'Intenta nuevamente.' });
      }
    } catch (err) {
      setToast({ visible: true, type: 'error', title: 'No se pudo eliminar', message: err.message || 'Intenta nuevamente.' });
    }
  };

  const handleOpenModal = (mode, proyecto = null) => {
    setModalMode(mode);
    setProyectoSeleccionado(proyecto);
    
    if (mode === 'editar' && proyecto) {
      const raw = proyecto.descripcion || '';
      const cleaned = cleanDescription(raw);
      const markers = extractMarkers(raw);
      setFormData({
        nombre_proyecto: proyecto.nombre_proyecto,
        descripcion: cleaned,
        id_jefe: proyecto.id_usuario,  // ← Usar id_usuario del backend
        fecha_inicio: proyecto.fecha_inicio || '',
        fecha_fin: proyecto.fecha_fin || ''
      });
      setDescMarkers(markers);
    } else {
      setFormData({
        nombre_proyecto: '',
        descripcion: '',
        id_jefe: '',
        fecha_inicio: new Date().toISOString().split('T')[0],
        fecha_fin: ''
      });
      setDescMarkers('');
    }
    
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setProyectoSeleccionado(null);
    setFormData({ nombre_proyecto: '', descripcion: '', id_jefe: '', fecha_inicio: '', fecha_fin: '' });
  };

  // Filtrado
  const proyectosFiltrados = proyectos.filter(proyecto => {
    const jefeNombre = proyecto.jefe_nombre || proyecto.jefe_proyecto?.nombre || '';
    const jefeId = proyecto.id_usuario || proyecto.jefe_id || proyecto.jefe_proyecto?.id;
    const matchBusqueda = proyecto.nombre_proyecto.toLowerCase().includes(busqueda.toLowerCase()) ||
      (jefeNombre && jefeNombre.toLowerCase().includes(busqueda.toLowerCase()));

    const matchJefe = filtroJefe === 'TODOS' || String(jefeId) === String(filtroJefe);

    const fi = proyecto.fecha_inicio ? new Date(proyecto.fecha_inicio) : null;
    const ff = proyecto.fecha_fin ? new Date(proyecto.fecha_fin) : null;
    let matchFecha = true;
    if (mesSeleccionado && anioSeleccionado) {
      const m = Number(mesSeleccionado);
      const y = Number(anioSeleccionado);
      const start = new Date(y, m - 1, 1);
      const end = new Date(y, m, 0);
      const inRange = (date) => !!date && date >= start && date <= end;
      const intersectsMonth = (!!fi && fi <= end) && (!ff || ff >= start);
      matchFecha = intersectsMonth || inRange(fi) || inRange(ff);
    }

    return matchBusqueda && matchJefe && matchFecha;
  });

  if (loading) {
    return (
      <div className="gestion-proyectos">
        <div className="loading">
          <div className="spinner"></div>
          <p>Cargando proyectos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gestion-proyectos">
        <div className="loading">
          <p style={{color:'#ef4444',fontWeight:700}}>Error: {error}</p>
          <button className="btn-nuevo" onClick={fetchProyectos}>Reintentar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="gestion-proyectos">
      <AnimatePresence>
        {toast.visible && (
          <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50">
            <motion.div
              initial={{ opacity: 0, y: -16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -16, scale: 0.98 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
            >
              <div className={`flex items-start gap-3 rounded-xl border shadow-lg px-4 py-3 ${toast.type === 'success' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                <div className={`${toast.type === 'success' ? 'text-emerald-600' : 'text-red-600'} text-xl`}>
                  {toast.type === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-800">{toast.title}</p>
                  <p className="text-sm text-slate-600">{toast.message}</p>
                </div>
                <button
                  onClick={() => setToast(prev => ({ ...prev, visible: false }))}
                  className="text-slate-400 hover:text-slate-600"
                  aria-label="Cerrar notificación"
                >
                  <FaTimes />
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      {/* Header */}
      <div className="proyectos-header">
        <div className="proyectos-title-section">
          <div className="proyectos-icon">
            <FaProjectDiagram />
          </div>
          <div>
            <h1>Gestión de Proyectos</h1>
            <p className="proyectos-subtitle">Administra proyectos y asignaciones</p>
          </div>
        </div>
        <button className="btn-nuevo" onClick={() => handleOpenModal('crear')}>
          <FaPlus />
          <span>Nuevo Proyecto</span>
        </button>
      </div>

      {/* Filtros */}
      <div className="filtros-section">
        <h2>Filtros de Búsqueda</h2>
        <div className="filtros-grid">
          <div className="filtro-item">
            <label>
              <FaSearch /> Búsqueda
            </label>
            <input
              type="text"
              placeholder="Buscar por nombre de proyecto o jefe..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>
          <div className="filtro-item">
            <label>
              <FaUserTie /> Jefe asignado
            </label>
            <select value={filtroJefe} onChange={(e) => setFiltroJefe(e.target.value)}>
              <option value="TODOS">Todos</option>
              {jefes.map(j => (
                <option key={j.id_usuario} value={String(j.id_usuario)}>{j.nombre}</option>
              ))}
            </select>
          </div>
          <div className="filtro-item">
            <label>
              <FaCalendarAlt /> Mes
            </label>
            <select value={mesSeleccionado} onChange={(e) => setMesSeleccionado(e.target.value)}>
              <option value="">Todos</option>
              <option value="1">Enero</option>
              <option value="2">Febrero</option>
              <option value="3">Marzo</option>
              <option value="4">Abril</option>
              <option value="5">Mayo</option>
              <option value="6">Junio</option>
              <option value="7">Julio</option>
              <option value="8">Agosto</option>
              <option value="9">Septiembre</option>
              <option value="10">Octubre</option>
              <option value="11">Noviembre</option>
              <option value="12">Diciembre</option>
            </select>
          </div>
          <div className="filtro-item">
            <label>
              <FaCalendarAlt /> Año
            </label>
            <select value={anioSeleccionado} onChange={(e) => setAnioSeleccionado(e.target.value)}>
              {Array.from({ length: 6 }).map((_, i) => {
                const y = String(new Date().getFullYear() - i);
                return <option key={y} value={y}>{y}</option>;
              })}
            </select>
          </div>
        </div>
        <div className="filtros-actions">
          <button className="btn-limpiar" onClick={() => { setFiltroJefe('TODOS'); setMesSeleccionado(''); setAnioSeleccionado(String(new Date().getFullYear())); setBusqueda(''); }}>Limpiar Filtros</button>
        </div>
      </div>

      {/* Tabla */}
      <div className="proyectos-tabla-container">
        <table className="proyectos-tabla">
          <thead>
            <tr>
              <th>ID</th>
              <th>Proyecto</th>
              <th>Descripción</th>
              <th>Jefe Asignado</th>
              <th>Estado</th>
              <th>Fecha Inicio</th>
              <th>Fecha Fin</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {proyectosFiltrados.length === 0 ? (
              <tr>
                <td colSpan="8" style={{textAlign: 'center', padding: '3rem', color: '#64748b'}}>
                  <FaProjectDiagram style={{fontSize: '3rem', marginBottom: '1rem', opacity: 0.3}} />
                  <p>No hay proyectos para mostrar</p>
                </td>
              </tr>
            ) : (
              proyectosFiltrados.map(proyecto => (
                <tr key={proyecto.id_proyecto}>
                  <td>{proyecto.id_proyecto}</td>
                  <td className="proyecto-nombre">{proyecto.nombre_proyecto}</td>
                  <td className="proyecto-descripcion">{cleanDescription(proyecto.descripcion) || '-'}</td>
                  <td>
                    <div className="jefe-info">
                      <FaUserTie />
                      <span>{proyecto.jefe_nombre || proyecto.jefe_proyecto?.nombre || 'Sin asignar'}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`estado-badge ${
                      (proyecto.estado === 'En Progreso' || proyecto.estado === 'ACTIVO') ? 'activo' :
                      (proyecto.estado === 'Completado' || proyecto.estado === 'COMPLETADO') ? 'activo' :
                      (proyecto.estado === 'Cancelado' || proyecto.estado === 'CANCELADO') ? 'rechazado' :
                      (proyecto.estado === 'Pausado') ? 'rechazado' :
                      'pendiente'
                    }`}>
                      {proyecto.estado === 'ACTIVO' || proyecto.estado === 'En Progreso' ? 'En Progreso' :
                       proyecto.estado === 'COMPLETADO' || proyecto.estado === 'Completado' ? 'Completado' :
                       proyecto.estado === 'CANCELADO' || proyecto.estado === 'Cancelado' ? 'Cancelado' :
                       proyecto.estado === 'Pausado' ? 'Pausado' :
                       proyecto.estado === 'PENDIENTE' ? 'Pendiente' :
                       proyecto.estado || 'Pendiente de Aceptación'}
                    </span>
                  </td>
                  <td>
                    {proyecto.fecha_inicio ? (
                      <div className="fecha-info">
                        <FaCalendarAlt />
                        <span>{(() => { const a=String(proyecto.fecha_inicio||''); const p=a.split('T')[0].split(' ')[0]; if(!p) return '-'; const [y,m,d]=p.split('-'); return `${d}/${m}/${y}`; })()}</span>
                      </div>
                    ) : '-'}
                  </td>
                  <td>
                    {proyecto.fecha_fin ? (
                      <div className="fecha-info">
                        <FaCalendarAlt />
                        <span>{(() => { const a=String(proyecto.fecha_fin||''); const p=a.split('T')[0].split(' ')[0]; if(!p) return '-'; const [y,m,d]=p.split('-'); return `${d}/${m}/${y}`; })()}</span>
                      </div>
                    ) : '-'}
                  </td>
                  <td className="acciones">
                    <button
                      className="btn-editar"
                      onClick={() => handleOpenModal('editar', proyecto)}
                      title="Editar"
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="btn-eliminar"
                      onClick={() => handleEliminarProyectoOpen(proyecto)}
                      title="Eliminar"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modalMode === 'crear' ? 'Crear Proyecto' : 'Editar Proyecto'}</h2>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            
            <form onSubmit={modalMode === 'crear' ? handleCrearProyecto : handleActualizarProyecto}>
              <div className="form-group">
                <label>Nombre del Proyecto *</label>
                <input
                  type="text"
                  value={formData.nombre_proyecto}
                  onChange={(e) => setFormData({...formData, nombre_proyecto: e.target.value})}
                  required
                  placeholder="Ej: Sistema de Gestión"
                />
              </div>

              <div className="form-group">
                <label>Descripción</label>
                <textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                  placeholder="Descripción del proyecto..."
                  rows="3"
                />
              </div>


              <div className="form-group">
                <label>Asignar Jefe de Proyecto *</label>
                <select
                  value={formData.id_jefe}
                  onChange={(e) => setFormData({...formData, id_jefe: e.target.value})}
                  required
                >
                  <option value="">Seleccionar jefe</option>
                  {jefes.map(jefe => (
                    <option key={jefe.id_usuario} value={jefe.id_usuario}>
                      {jefe.nombre} ({jefe.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Fecha de Creación</label>
                  <input
                    type="date"
                    value={formData.fecha_inicio}
                    onChange={(e) => setFormData({...formData, fecha_inicio: e.target.value})}
                    readOnly={modalMode === 'crear'}
                    disabled={modalMode === 'crear'}
                  />
                </div>

                <div className="form-group">
                  <label>Fecha Plazo Establecida</label>
                  <input
                    type="date"
                    value={formData.fecha_fin}
                    onChange={(e) => setFormData({...formData, fecha_fin: e.target.value})}
                    min={formData.fecha_inicio || new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancelar" onClick={handleCloseModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn-guardar">
                  {modalMode === 'crear' ? 'Enviar Solicitud' : 'Guardar Cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {confirmEliminarOpen && (
        <div className="modal-overlay" onClick={closeConfirmEliminar}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Eliminar proyecto</h2>
              <button className="modal-close" onClick={closeConfirmEliminar}>×</button>
            </div>
            <div className="p-3">
              <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                <div className="text-red-600 text-xl">
                  <FaExclamationCircle />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-800">¿Estás seguro?</p>
                  <p className="text-sm text-slate-600">Se eliminará el proyecto <strong>{proyectoParaEliminar?.nombre_proyecto}</strong>. Esta acción no se puede deshacer.</p>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-nuevo" onClick={closeConfirmEliminar}>Cancelar</button>
              <button type="button" className="btn-eliminar" onClick={confirmEliminarProyecto}>Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GestionProyectos;
